import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { GatewayTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tokens/models/outputs/gateway-token.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { TokenDeleteController } from '@app/feature/token/token-delete/token-delete.controller';
import { TokenDetailsView } from '@app/feature/token/token-details/token-details.view';
import { TokenUpdateController } from '@app/feature/token/token-update/token-update.controller';
import { TokenContextProvider } from '@app/feature/token/token.context';
import { useCpmsBoGetTokenQuery } from '@service/cpms-bo-token.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import { useSetPageTitleContext } from '@shared/layout/page-layout/page-layout-title.context-provider';

interface TokenDetailsControllerProps {
  resource: GatewayTokenModel;
}

export const TokenDetailsController = ({
  resource,
}: TokenDetailsControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.authId);
  }, [resource, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.TOKENS,
        action: ActionsByResource.TOKENS.UPDATE,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="update"
          icon="edit-05"
          onClick={() => setIsUpdateDialogOpen(true)}
        />,
      );
    }
    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.TOKENS,
        action: ActionsByResource.TOKENS.DELETE,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="delete"
          icon="trash-01"
          onClick={() => setIsDeleteDialogOpen(true)}
        />,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <TokenContextProvider data={resource}>
      <TokenDetailsView />

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}>
        <Dialog.Header
          title={t('$.token-delete.title')}
          icon="map-01"
          onClose={() => setIsDeleteDialogOpen(false)}
        />
        <Dialog.Content>
          <TokenDeleteController
            onCancel={() => setIsDeleteDialogOpen(false)}
            onSuccess={() => setIsDeleteDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>

      <Dialog
        open={isUpdateDialogOpen}
        onClose={() => setIsUpdateDialogOpen(false)}
        confirmClose
        height="100%"
        size={DialogSize.LARGE}>
        <Dialog.Header
          title={t('$.token-update.title')}
          icon="map-01"
          onClose={() => setIsUpdateDialogOpen(false)}
        />
        <Dialog.Content>
          <TokenUpdateController
            onCancel={() => setIsUpdateDialogOpen(false)}
            onSuccess={() => setIsUpdateDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
    </TokenContextProvider>
  );
};

export const TokenDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetTokenQuery(params.id ?? '');

  const { Component } = withProtectedResource<
    GatewayTokenModel,
    {},
    GatewayTokenModel
  >(TokenDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.TOKENS,
        action: ActionsByResource.TOKENS.READ,
      }),
  })({});

  return Component;
};
